import { FeatureAccess, UserTier, FeatureType } from '@/models/feature-restriction.model';

// Define which features are available for each tier
export const FEATURE_ACCESS: FeatureAccess = {
  custom_domain: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  advanced_analytics: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  api_access: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  team_collaboration: {
    free: false,
    pro: false,
    startup: true,
    enterprise: true,
  },
  priority_support: {
    free: false,
    pro: false,
    startup: true,
    enterprise: true,
  },
  unlimited_projects: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  custom_integrations: {
    free: false,
    pro: false,
    startup: true,
    enterprise: true,
  },
  white_label: {
    free: false,
    pro: false,
    startup: false,
    enterprise: true,
  },
  advanced_deployment: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  backup_restore: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  sso_integration: {
    free: false,
    pro: false,
    startup: true,
    enterprise: true,
  },
  webhook_access: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  advanced_ai_features: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  custom_branding: {
    free: false,
    pro: false,
    startup: true,
    enterprise: true,
  },
  export_data: {
    free: false,
    pro: true,
    startup: true,
    enterprise: true,
  },
  credit_topup: {
    free: true,
    pro: true,
    startup: true,
    enterprise: true,
  },
};

// Feature descriptions and upgrade messages
export const FEATURE_DESCRIPTIONS: Record<FeatureType, {
  name: string;
  description: string;
  upgradeMessage: string;
  upgradeDescription: string;
  icon: string;
}> = {
  custom_domain: {
    name: 'Custom Domain',
    description: 'Use your own domain name for your applications',
    upgradeMessage: 'Custom domains are available in Pro plan and above',
    upgradeDescription: 'Get your own domain name and professional branding for your applications.',
    icon: '🌐',
  },
  advanced_analytics: {
    name: 'Advanced Analytics',
    description: 'Detailed insights and performance metrics',
    upgradeMessage: 'Advanced analytics are available in Pro plan and above',
    upgradeDescription: 'Get detailed insights into your application performance and user behavior.',
    icon: '📊',
  },
  api_access: {
    name: 'API Access',
    description: 'Access to REST APIs and webhooks',
    upgradeMessage: 'API access is available in Pro plan and above',
    upgradeDescription: 'Integrate your applications with external services using our APIs.',
    icon: '🔌',
  },
  team_collaboration: {
    name: 'Team Collaboration',
    description: 'Work together with your team members',
    upgradeMessage: 'Team collaboration is available in Startup plan and above',
    upgradeDescription: 'Invite team members and collaborate on projects together.',
    icon: '👥',
  },
  priority_support: {
    name: 'Priority Support',
    description: 'Get faster response times for support requests',
    upgradeMessage: 'Priority support is available in Startup plan and above',
    upgradeDescription: 'Get faster response times and dedicated support for your projects.',
    icon: '⚡',
  },
  unlimited_projects: {
    name: 'Unlimited Projects',
    description: 'Create as many projects as you need',
    upgradeMessage: 'Unlimited projects are available in Pro plan and above',
    upgradeDescription: 'Create unlimited projects without any restrictions.',
    icon: '♾️',
  },
  custom_integrations: {
    name: 'Custom Integrations',
    description: 'Build custom integrations with third-party services',
    upgradeMessage: 'Custom integrations are available in Startup plan and above',
    upgradeDescription: 'Build custom integrations with your favorite tools and services.',
    icon: '🔗',
  },
  white_label: {
    name: 'White Label',
    description: 'Remove SuperEngineer branding and use your own',
    upgradeMessage: 'White label is available in Enterprise plan only',
    upgradeDescription: 'Remove all SuperEngineer branding and use your own company branding.',
    icon: '🏷️',
  },
  advanced_deployment: {
    name: 'Advanced Deployment',
    description: 'Advanced deployment options and configurations',
    upgradeMessage: 'Advanced deployment is available in Pro plan and above',
    upgradeDescription: 'Access advanced deployment options and configurations.',
    icon: '🚀',
  },
  backup_restore: {
    name: 'Backup & Restore',
    description: 'Automated backups and easy restore functionality',
    upgradeMessage: 'Backup & restore is available in Pro plan and above',
    upgradeDescription: 'Keep your data safe with automated backups and easy restore.',
    icon: '💾',
  },
  sso_integration: {
    name: 'SSO Integration',
    description: 'Single Sign-On integration with your identity provider',
    upgradeMessage: 'SSO integration is available in Startup plan and above',
    upgradeDescription: 'Integrate with your existing identity provider for seamless authentication.',
    icon: '🔐',
  },
  webhook_access: {
    name: 'Webhook Access',
    description: 'Real-time notifications and event handling',
    upgradeMessage: 'Webhook access is available in Pro plan and above',
    upgradeDescription: 'Get real-time notifications and handle events with webhooks.',
    icon: '🔔',
  },
  advanced_ai_features: {
    name: 'Advanced AI Features',
    description: 'Access to advanced AI capabilities and models',
    upgradeMessage: 'Advanced AI features are available in Pro plan and above',
    upgradeDescription: 'Unlock advanced AI capabilities and access to premium models.',
    icon: '🤖',
  },
  custom_branding: {
    name: 'Custom Branding',
    description: 'Customize the look and feel of your applications',
    upgradeMessage: 'Custom branding is available in Startup plan and above',
    upgradeDescription: 'Customize the appearance and branding of your applications.',
    icon: '🎨',
  },
  export_data: {
    name: 'Export Data',
    description: 'Export your project data and configurations',
    upgradeMessage: 'Data export is available in Pro plan and above',
    upgradeDescription: 'Export your project data and configurations for backup or migration.',
    icon: '📤',
  },
  credit_topup: {
    name: 'Credit Top-up',
    description: 'Purchase additional credits for your account',
    upgradeMessage: 'Credit top-up is available for all plans',
    upgradeDescription: 'Purchase additional credits to extend your usage limits whenever you need.',
    icon: '💳',
  },
};

// Helper function to check if a feature is available for a tier
export const hasFeatureAccess = (feature: FeatureType, tier: UserTier): boolean => {
  return FEATURE_ACCESS[feature]?.[tier] || false;
};

// Helper function to get the minimum tier required for a feature
export const getRequiredTier = (feature: FeatureType): UserTier => {
  const access = FEATURE_ACCESS[feature];
  if (!access) return 'enterprise';

  for (const [tier, hasAccess] of Object.entries(access)) {
    if (hasAccess) {
      return tier as UserTier;
    }
  }

  return 'enterprise';
};

// Helper function to get all available features for a tier
export const getAvailableFeatures = (tier: UserTier): FeatureType[] => {
  return Object.keys(FEATURE_ACCESS).filter(feature =>
    hasFeatureAccess(feature as FeatureType, tier)
  ) as FeatureType[];
};
